import { useEffect, type SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Autocomplete from 'components/Common/Autocomplete';
import { ParishUsersAutocompleteProps } from 'types/autocomplete';
import { AppDispatch, RootState } from 'redux/store';
import { fetchParishUsers } from 'redux/slices/user/parishUsers';
import { selectSelectedParishId } from 'redux/selectors/parishContext';
import AutocompleteOption from 'components/Common/autocomplete/AutocompleteOption';
import { ParishUserOption } from 'types/user';

const ParishUsersAutocomplete = ({ user, setUser, excludeUsers, parishId: parishIdProp, ...props }: ParishUsersAutocompleteProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const selectedParishId = useSelector(selectSelectedParishId);
    const parishId = parishIdProp || selectedParishId;
    const { loading = false, records = [] } = useSelector((state: RootState) => state?.parishUsers || {});
    const filteredUsers = excludeUsers?.length
        ? records.filter((option) => {
              const keys = [option?.id, option?.userUUID].map(String);
              return !excludeUsers.map(String).some((ex) => keys.includes(ex));
          })
        : records;

    if (user && typeof user === 'string') {
        user = filteredUsers.find((u) => u.userUUID === user || u.id === user || u.username === user) ?? null;
    } else if (props.value && typeof props.value === 'string') {
        props.value = filteredUsers.find((u) => u.userUUID === props.value || u.id === props.value || u.username === props.value);
    }

    useEffect(() => {
        if (!parishId) return;
        dispatch(fetchParishUsers({ parishId }));
    }, [dispatch, parishId]);

    return (
        <Autocomplete
            loading={loading}
            value={user}
            options={filteredUsers}
            onChange={(event: SyntheticEvent, next: ParishUserOption, ...rest) => {
                const { onChange } = props;
                if (setUser) setUser(next);
                if (onChange) onChange(event, next, ...rest);
            }}
            getOptionLabel={(option) => option?.name || ''}
            renderOption={(optionProps, option) => <AutocompleteOption option={option} props={optionProps} />}
            getOptionKey={(option) => (typeof option === 'object' ? option?.userUUID || option?.id : option)}
            {...props}
        />
    );
};

export default ParishUsersAutocomplete;
